// Consent state for optional (analytics) cookies.
//
// Single source of truth shared by the cookie banner, the Google Analytics
// loader, and the footer "Cookie settings" link. They coordinate through window
// events rather than a React context provider — app/layout.tsx is sensitive to
// wrapping changes (see the React #310 note there), so we avoid adding a
// provider around the tree.

export const CONSENT_KEY = "loyeecards-cookie-consent";
export const CONSENT_CHANGE_EVENT = "loyeecards:consent-change";
export const CONSENT_OPEN_EVENT = "loyeecards:consent-open";

export type ConsentValue = "granted" | "denied";

// Reads the stored choice. Returns null when undecided, when the stored value is
// a legacy/unknown string (e.g. the old "dismissed" notice value), or when
// storage is unavailable (private browsing) — all of which mean "ask again".
export function getConsent(): ConsentValue | null {
  if (typeof window === "undefined") return null;
  try {
    const v = window.localStorage.getItem(CONSENT_KEY);
    return v === "granted" || v === "denied" ? v : null;
  } catch {
    return null;
  }
}

// Persists the choice and notifies listeners (banner, GA loader) in the same tab.
export function setConsent(value: ConsentValue): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(CONSENT_KEY, value);
  } catch {
    // localStorage unavailable — still dispatch so in-memory UI updates.
  }
  window.dispatchEvent(new Event(CONSENT_CHANGE_EVENT));
}

// Re-opens the banner so a user can change a previous choice (GDPR withdrawal).
export function openConsentBanner(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(CONSENT_OPEN_EVENT));
}

// Best-effort deletion of cookies whose name matches `match`, across the
// path/domain combinations Google uses (host, .host, and the registrable root
// domain). Shared by clearGaCookies and clearMarketingCookies.
function deleteCookies(match: (name: string) => boolean): void {
  if (typeof document === "undefined") return;

  const names = document.cookie
    .split(";")
    .map((c) => c.split("=")[0].trim())
    .filter(match);

  const { hostname } = window.location;
  const domains = [hostname, "." + hostname];
  const rootDomain = hostname.split(".").slice(-2).join(".");
  if (rootDomain !== hostname) domains.push("." + rootDomain);

  const expired = "expires=Thu, 01 Jan 1970 00:00:00 GMT";
  for (const name of names) {
    document.cookie = `${name}=; path=/; ${expired}`;
    for (const d of domains) {
      document.cookie = `${name}=; path=/; domain=${d}; ${expired}`;
    }
  }
}

// Deletes Google Analytics cookies (_ga, _ga_*, _gid) so withdrawing consent
// actually clears them.
export function clearGaCookies(): void {
  deleteCookies((name) => name.startsWith("_ga") || name === "_gid");
}

// Deletes Google Ads / conversion-linker cookies (_gcl_*, _gac_*, FPAU) set by
// tags fired through Google Tag Manager, so withdrawing consent actually clears
// them.
export function clearMarketingCookies(): void {
  deleteCookies(
    (name) =>
      name.startsWith("_gcl") || name.startsWith("_gac") || name === "FPAU"
  );
}

// --- Consent Mode v2 (Google Tag Manager) ---------------------------------
//
// The container itself stays gated behind consent (see google-tag-manager.tsx),
// so these signals only ever describe an opted-in visitor. They exist so the
// Google (Ads/GA) tags fired through GTM receive explicit Consent Mode v2
// signals — what Google's post-2024 EEA rules expect to unlock compliant
// conversion tracking + personalised-ads/remarketing for users who accepted.

// The four Consent Mode v2 signal keys read by GTM-fired Google tags.
const GTM_CONSENT_KEYS = [
  "ad_storage",
  "ad_user_data",
  "ad_personalization",
  "analytics_storage",
] as const;

function gtmConsentSignals(state: ConsentValue): Record<string, ConsentValue> {
  return Object.fromEntries(GTM_CONSENT_KEYS.map((k) => [k, state]));
}

// Inline-script body that defines the gtag shim and sets the Consent Mode v2
// DEFAULT for all four signals. Rendered (via next/script) immediately before
// the GTM container so the signal is queued in the dataLayer as the container
// initialises. We only render it once consent is granted, so it is "granted".
export function gtmConsentDefaultScript(state: ConsentValue): string {
  return [
    "window.dataLayer = window.dataLayer || [];",
    "function gtag(){dataLayer.push(arguments);}",
    `gtag('consent','default',${JSON.stringify(gtmConsentSignals(state))});`,
  ].join("");
}

// Runtime Consent Mode v2 `update` push, for a live consent change (e.g. a
// withdrawal) that must reach an already-loaded container without a reload.
// No-op on the server.
export function setGtmConsent(state: ConsentValue): void {
  if (typeof window === "undefined") return;
  const w = window as unknown as {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  };
  w.dataLayer = w.dataLayer || [];
  if (typeof w.gtag !== "function") {
    w.gtag = function gtag() {
      // gtag pushes its `arguments` object; GTM's consent API depends on that
      // exact shape, so we must not substitute a plain array here.
      // eslint-disable-next-line prefer-rest-params
      w.dataLayer!.push(arguments);
    };
  }
  w.gtag("consent", "update", gtmConsentSignals(state));
}
